'use client';

import { getLocalTimeZone } from '@internationalized/date';
import { differenceInCalendarDays } from 'date-fns';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { useInteractiveLoader } from '~/components/PartnerLoader/PartnerLoader';
import { useConfirmTripDetails } from '~/hooks/useConfirmTripDetails';
import { useToast } from '~/hooks/useToasts';
import { useUpdateTrip } from '~/hooks/useUpdateTrip';
import { type CalendarRange, getSafeRange, getTripDurationDaysFromStayNights } from '~/utils/dateHelpers/calendarRange';
import { useTripOverviewSummary } from './useTripOverviewSummary';
export const useTripDateSelection = () => {
  const {
    tripId,
    firstItem,
    lastItem,
    nights,
    isDateUnset,
    datesConfirmed
  } = useTripOverviewSummary();
  const t = useTranslations('journey');
  const toast = useToast();
  const updateTrip = useUpdateTrip();
  const {
    startLoader,
    completeProgressBar
  } = useInteractiveLoader();
  const confirmTripDetails = useConfirmTripDetails();
  const [localRange, setLocalRange] = useState<CalendarRange | null>(null);
  const [clearCount, setClearCount] = useState(0);
  const bookingStartDate = firstItem?.startDate ?? '';
  const bookingEndDate = lastItem?.endDate ?? '';
  const serverRange = !isDateUnset && bookingStartDate && bookingEndDate ? getSafeRange(bookingStartDate, bookingEndDate) : null;
  const fallbackDurationDays = getTripDurationDaysFromStayNights(nights);
  const unsetDuration = isDateUnset ? fallbackDurationDays : null;
  const originalDuration = serverRange ? serverRange.end.compare(serverRange.start) + 1 : unsetDuration;
  const range = localRange ?? serverRange ?? getSafeRange('', '', fallbackDurationDays);
  const hasSelectedRange = localRange !== null || serverRange !== null;
  const commitRange = async (target: CalendarRange): Promise<string | null> => {
    const timeZone = getLocalTimeZone();
    startLoader();
    try {
      const result = await updateTrip({
        startDate: target.start.toDate(timeZone),
        endDate: target.end.toDate(timeZone),
        duration: differenceInCalendarDays(target.end.toDate(timeZone), target.start.toDate(timeZone)) + 1
      });
      if (result.error) {
        toast?.add({
          title: result.error,
          variant: 'error',
          position: 'middle'
        }, {
          timeout: 5000
        });
        return null;
      }
      return result.tripId ?? null;
    } finally {
      completeProgressBar();
    }
  };
  const showDatesConfirmedToast = () => toast?.add({
    title: t('overview_dates_success'),
    variant: 'success',
    position: 'middle'
  }, {
    timeout: 5000
  });
  const confirmDates = async (): Promise<boolean> => {
    if (!localRange) {
      if (!serverRange) return false;
      confirmTripDetails(tripId, showDatesConfirmedToast);
      return true;
    }
    const newTripId = await commitRange(localRange);
    if (!newTripId) return false;
    setLocalRange(null);
    confirmTripDetails(newTripId, showDatesConfirmedToast);
    return true;
  };
  const clearDates = (): void => {
    setLocalRange(null);
    setClearCount(count => count + 1);
  };
  return {
    localRange,
    setLocalRange,
    range,
    calendarKey: `${tripId}-${clearCount}`,
    hasSelectedRange,
    originalDuration,
    datesConfirmed,
    confirmDates,
    clearDates
  };
};